import { z } from 'zod';

import {
  WorkflowDtoStatus,
  WorkflowGoogleDriveFileContextDto,
  WorkflowMeetingsContextDtoScope,
  WorkflowMeetingsContextDtoTimeRange,
  WorkflowScheduledTriggerDtoDaysOfWeekItem
} from '@/api/generated';

export const formSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  status: z.nativeEnum(WorkflowDtoStatus),
  prompt: z.string().min(1, 'Prompt is required'),
  trigger: z.array(z.nativeEnum(WorkflowScheduledTriggerDtoDaysOfWeekItem)),
  scope: z.nativeEnum(WorkflowMeetingsContextDtoScope),
  timeRange: z.string().min(2, 'Focus is required'),
  keyWords: z.array(z.string().min(1)).default([]),
  notifications: z.object({
    email: z.boolean().optional(),
    slack: z.boolean().optional()
  }),
  createdFrom: z.string().optional()
});

export type LocalCapabilityType = {
  id?: string;
  title?: string;
  status?: WorkflowDtoStatus;
  prompt?: string;
  trigger?: WorkflowScheduledTriggerDtoDaysOfWeekItem[];
  scope?: WorkflowMeetingsContextDtoScope;
  timeRange?: WorkflowMeetingsContextDtoTimeRange;
  keyWords?: string[];
  notifications?: {
    email?: boolean;
    slack?: boolean;
  } | null;
  createdAt?: string;
  updatedAt?: string;
  createdFrom?: string | null;
  googleDrive?: {
    files: WorkflowGoogleDriveFileContextDto[];
  };
};

export const validate = (values: FormValues) => {
  try {
    formSchema.parse(values);
    return {};
  } catch (e: unknown) {
    const errors: Record<string, string> = {};
    if (e instanceof z.ZodError) {
      e.errors.forEach((err: z.ZodIssue) => {
        if (err.path && err.path.length > 0) {
          errors[err.path[0]] = err.message;
        }
      });
    }
    return errors;
  }
};

export type FormValues = z.infer<typeof formSchema>;

export const TIME_RANGE_OPTIONS = [
  {
    label: "Yesterday's meetings",
    value: WorkflowMeetingsContextDtoTimeRange.yesterday
  },
  {
    label: 'Last 15 meetings',
    value: WorkflowMeetingsContextDtoTimeRange.last15Meetings
  },

  {
    label: 'Last 5 meetings',
    value: WorkflowMeetingsContextDtoTimeRange.last5Meetings
  }
];

// Get all days of week as an array
export const ALL_DAYS_OF_WEEK = Object.values(WorkflowScheduledTriggerDtoDaysOfWeekItem);

export const triggerDisplay = (trigger: WorkflowScheduledTriggerDtoDaysOfWeekItem[]): string =>
  trigger.length === 7 ? 'Every Day' : 'Every Monday';

export const DEFAULT_TIME_RANGE = '0';

export const initialValues: FormValues = {
  title: '',
  status: WorkflowDtoStatus.enabled,
  prompt: '',
  trigger: ALL_DAYS_OF_WEEK,
  scope: WorkflowMeetingsContextDtoScope.all,
  timeRange: DEFAULT_TIME_RANGE,
  keyWords: [],
  notifications: {
    email: true
  }
};
